import { Close } from "assets/icons/Close";
import ImageIcon from "assets/icons/ImageIcon";
import ImagePlaceholder from "assets/images/dapps/imagePlaceholder.png";
import { Button } from "components/Button";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { fileApi } from "services/files";
import { MIN_IMG_FILE_SIZE } from "screens/DApps/constants";
import { ProjectFormInput } from "screens/DApps/components/Pannel/components/ProjectFormInput";
import { DateRangePicker, Switch } from "@heroui/react";
import type { RangeValue } from "@react-types/shared";
import type { DateValue } from "@react-types/datepicker";
import { ICampaign } from "screens/Campaigns/types";
import cn from "clsx";
import { campaignsApi } from "screens/Campaigns/service";
import { useCampaignsStore } from "screens/Campaigns/store";
import { CalendarDate } from "@internationalized/date";
import { CampaignsRules } from "../CampaignsRules";
import { SquareLoader } from "screens/Main/components/Loader/SquareLoader";
import { useNotify } from "hooks/useToast/useToast";
import { Rewards } from "./Rewards";

export const CampaignAddForm = ({
  isOpen,
  onClose,
  getAllCampaignsHandler
}: {
  isOpen: boolean;
  onClose: () => void;
  getAllCampaignsHandler: () => void;
}) => {
  const [mainBannerPreview, setMainBannerPreview] =
    useState<string>(ImagePlaceholder);
  const [mainBannerFile, setMainBannerFile] = useState<File | null>(null);
  const [mainBannerSizeError, setMainBannerSizeError] = useState(false);
  const [widgetBannerPreview, setWidgetBannerPreview] =
    useState<string>(ImagePlaceholder);
  const [widgetBannerFile, setWidgetBannerFile] = useState<File | null>(null);
  const [widgetBannerSizeError, setWidgetBannerSizeError] = useState(false);
  const [isActiveCampaign, setIsActiveCampaign] = useState(false);
  const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(
    null
  );
  const [isUploadingMainBanner, setIsUploadingMainBanner] = useState(false);
  const [isUploadingWidgetBanner, setIsUploadingWidgetBanner] = useState(false);
  const { notify } = useNotify();
  const { selectedCampaigns: selectedCampaign, setSelectedCampaign, isCampaignIteracting } =
    useCampaignsStore();

  const isEditMode = !!selectedCampaign;

  const { handleSubmit, watch, reset, control, setValue } = useForm<ICampaign>({
    defaultValues: {
      title: "",
      banner_url: "",
      secondary_banner_url: "",
      is_active: false,
      start_date: undefined,
      end_date: undefined,
      campaign_rules: [],
      discord_role_id: "",
      rewards: []
    },
    mode: "all"
  });

  const mainBanner = watch("banner_url");
  const widgetBanner = watch("secondary_banner_url");
  const rewards = watch("rewards");

  // Fill form with selected campaign data when it changes
  useEffect(() => {
    if (selectedCampaign && isOpen) {
      setValue("title", selectedCampaign.title || "");
      setValue("banner_url", selectedCampaign.banner_url || "");
      setValue(
        "secondary_banner_url",
        selectedCampaign.secondary_banner_url || ""
      );
      setValue("is_active", selectedCampaign.is_active || false);
      setValue("start_date", selectedCampaign.start_date);
      setValue("end_date", selectedCampaign.end_date);
      setValue("discord_role_id", selectedCampaign.discord_role_id || "");
      setValue("rewards", selectedCampaign.rewards || []);
      setIsActiveCampaign(selectedCampaign.is_active || false);

      // Set date range for the date picker
      if (selectedCampaign.start_date && selectedCampaign.end_date) {
        const startDate = new Date(selectedCampaign.start_date);
        const endDate = new Date(selectedCampaign.end_date);

        const start = new CalendarDate(
          startDate.getFullYear(),
          startDate.getMonth() + 1,
          startDate.getDate()
        );

        const end = new CalendarDate(
          endDate.getFullYear(),
          endDate.getMonth() + 1,
          endDate.getDate()
        );

        setDateRange({ start, end });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign, isOpen]);

  // Reset form and selected campaign on close
  useEffect(() => {
    if (!isOpen) {
      reset();
      if (setSelectedCampaign) {
        setSelectedCampaign(null);
      }
      setIsActiveCampaign(false);
      setDateRange(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onSubmit: SubmitHandler<ICampaign> = (data) => {
    if (selectedCampaign) {
      const currentDate = new Date();
      const campaignEndDate = new Date(selectedCampaign.end_date);

      if (campaignEndDate < currentDate && data.is_active) {
        notify({
          meassage:
            "You can't activate campaign of the end date has already passed",
          type: "info"
        });
        return;
      }

      campaignsApi.updateCampaign({ ...data, id: selectedCampaign.id }, () => {
        setMainBannerPreview(ImagePlaceholder);
        setWidgetBannerPreview(ImagePlaceholder);
        reset();
        if (setSelectedCampaign) {
          setSelectedCampaign(null);
        }
        onClose();
        getAllCampaignsHandler();
      });
    } else {
      // Create new campaign
      campaignsApi.createCampaign({ ...data }, () => {
        setMainBannerPreview(ImagePlaceholder);
        setWidgetBannerPreview(ImagePlaceholder);
        reset();
        onClose();
        getAllCampaignsHandler();
      });
    }
  };

  // Using isSubmitDisabled to ensure all required fields are filled
  const isSubmitDisabled =
    !watch("secondary_banner_url") ||
    !watch("banner_url") ||
    !watch("start_date") ||
    !watch("end_date") ||
    isUploadingMainBanner ||
    isUploadingWidgetBanner;

  const handleMainBannerFileChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > MIN_IMG_FILE_SIZE) {
        setMainBannerSizeError(true);
        return;
      }
      setMainBannerSizeError(false);
      setMainBannerFile(file);
    }
  };

  const handleWidgetBannerFileChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > MIN_IMG_FILE_SIZE) {
        setWidgetBannerSizeError(true);
        return;
      }
      setWidgetBannerSizeError(false);
      setWidgetBannerFile(file);
    }
  };

  const uploadMainBannerImage = async (file: File) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    setMainBannerSizeError(false);

    if (!allowedTypes.includes(file.type)) {
      setMainBannerSizeError(true);
      return;
    }

    setIsUploadingMainBanner(true);
    const url = await fileApi.uploadImage(file);
    setValue("banner_url", url);
    setIsUploadingMainBanner(false);
  };

  const uploadWidgetBannerImage = async (file: File) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    setWidgetBannerSizeError(false);

    if (!allowedTypes.includes(file.type)) {
      setWidgetBannerSizeError(true);
      return;
    }

    setIsUploadingWidgetBanner(true);
    const url = await fileApi.uploadImage(file);
    setValue("secondary_banner_url", url);
    setIsUploadingWidgetBanner(false);
  };

  useEffect(() => {
    if (mainBannerFile) {
      uploadMainBannerImage(mainBannerFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainBannerFile]);

  useEffect(() => {
    if (widgetBannerFile) {
      uploadWidgetBannerImage(widgetBannerFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetBannerFile]);

  useEffect(() => {
    if (dateRange?.start)
      setValue("start_date", dateRange?.start.toDate("UTC"));
    if (dateRange?.end) setValue("end_date", dateRange?.end.toDate("UTC"));
  }, [dateRange, setValue]);

  useEffect(() => {
    if (mainBannerSizeError) {
      const timer = setTimeout(() => setMainBannerSizeError(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [mainBannerSizeError]);

  useEffect(() => {
    setValue("is_active", isActiveCampaign);
  }, [isActiveCampaign, setValue]);

  const handleAddReward = () => {
    setValue("rewards", [...rewards, ""]);
  };

  const handleChangeReward = (index: number, value: string) => {
    const updatedRewards = [...rewards];
    updatedRewards[index] = value;
    setValue("rewards", updatedRewards);
  };

  const handleDeleteReward = (index: number) => {
    const updatedRewards = [...rewards];
    updatedRewards.splice(index, 1);
    setValue("rewards", updatedRewards);
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed z-4 bg-black/40 top-0 left-0 right-0 bottom-0 w-full"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed w-full md:w-[487px] h-[100svh] overflow-y-auto custom-scrollbar px-[20px] md:px-[40px] pt-[60px] top-0 bottom-0 right-0 bg-black md:border-l-[1px] z-5 transform transition-transform duration-300 ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="flex text-[24px] font-[700] leading-[32px] justify-between items-center mb-[40px]">
          <h3>{selectedCampaign ? "Edit campaign" : "Add new campaign"}</h3>
          <button onClick={onClose}>
            <Close />
          </button>
        </div>

        <form
          id="campaign-add-form"
          onSubmit={handleSubmit(onSubmit)}
          className={`flex flex-col gap-[24px] h-fit`}
        >
          <div>
            <p className="ml-1 mb-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
              Main banner
            </p>
            <div className="flex flex-col gap-[24px] w-full">
              <div
                className={cn(
                  "py-4 w-[308px] h-[92px] rounded-xl overflow-hidden custom-scrollbar flex justify-center items-center bg-[#FFFFFF1A]",
                  {
                    "!py-0": mainBannerFile
                  }
                )}
              >
                <img
                  src={
                    mainBanner ||
                    selectedCampaign?.banner_url ||
                    mainBannerPreview
                  }
                  alt=""
                  className="w-full h-full object-contain object-center"
                />
              </div>

              <div className="flex flex-col gap-[8px] justify-center">
                <input
                  id="main-banner"
                  type="file"
                  accept=".jpeg, .jpg, .png"
                  style={{ display: "none" }}
                  onChange={handleMainBannerFileChange}
                />
                <label
                  htmlFor="main-banner"
                  className="w-[180px] h-[43px] bg-[#FFFFFF1A] flex items-center justify-center rounded-full"
                >
                  <button
                    type="button"
                    className="px-3 flex items-center justify-center gap-[10px] text-[14px] font-[500] leading-[18.9px]"
                    onClick={() =>
                      document.getElementById("main-banner")?.click()
                    }
                  >
                    {isUploadingMainBanner ? (
                      <div className="flex w-full my-[20px] justify-center items-center">
                        <SquareLoader size={16} />
                      </div>
                    ) : (
                      <>
                        <ImageIcon />
                        Upload main banner
                      </>
                    )}
                  </button>
                </label>
                <p
                  className={`text-[12px] font-[500] ${mainBannerSizeError ? "text-red-500" : "opacity-70"
                    }`}
                >
                  {mainBannerSizeError
                    ? "Maximum image size 5 MB"
                    : "max 5mb, .jpeg, .png"}
                </p>
              </div>
            </div>
          </div>

          <div>
            <p className="ml-1 mb-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
              Widget banner
            </p>
            <div className="flex flex-col gap-[24px] w-full">
              <div
                className={cn(
                  "py-4 w-[246px] h-[200px] rounded-xl overflow-hidden custom-scrollbar flex justify-center items-center bg-[#FFFFFF1A]",
                  {
                    "!py-0": widgetBannerFile
                  }
                )}
              >
                <img
                  src={
                    widgetBanner ||
                    selectedCampaign?.secondary_banner_url ||
                    widgetBannerPreview
                  }
                  alt=""
                  className="w-full h-full object-contain object-center"
                />
              </div>

              <div className="flex flex-col gap-[8px] justify-center">
                <input
                  id="widget-banner"
                  type="file"
                  accept=".jpeg, .jpg, .png"
                  style={{ display: "none" }}
                  onChange={handleWidgetBannerFileChange}
                />
                <label
                  htmlFor="widget-banner"
                  className="w-[190px] h-[43px] bg-[#FFFFFF1A] flex items-center justify-center rounded-full"
                >
                  <button
                    type="button"
                    className="px-3 flex items-center justify-center gap-[10px] text-[14px] font-[500] leading-[18.9px]"
                    onClick={() =>
                      document.getElementById("widget-banner")?.click()
                    }
                  >
                    {isUploadingWidgetBanner ? (
                      <div className="flex w-full my-[20px] justify-center items-center">
                        <SquareLoader size={16} />
                      </div>
                    ) : (
                      <>
                        <ImageIcon />
                        Upload widget banner
                      </>
                    )}
                  </button>
                </label>
                <p
                  className={`text-[12px] font-[500] ${widgetBannerSizeError ? "text-red-500" : "opacity-70"
                    }`}
                >
                  {widgetBannerSizeError
                    ? "Maximum image size 5 MB"
                    : "max 5mb, .jpeg, .png"}
                </p>
              </div>
            </div>
          </div>

          <ProjectFormInput
            title="Campaign title"
            placeholder="Add title here"
            control={control}
            name="title"
          />

          <div className="flex items-center justify-between">
            <p className="ml-1 mb-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
              Is active
            </p>
            <Switch
              isDisabled={selectedCampaign ? false : true}
              isSelected={isActiveCampaign}
              onValueChange={setIsActiveCampaign}
              size="sm"
              classNames={{
                wrapper: "bg-[#FFFFFF1A]"
              }}
            />
          </div>

          <div>
            <p className="ml-1 mb-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
              Duration
            </p>
            <div className="w-full flex flex-col gap-y-2">
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                classNames={{
                  inputWrapper:
                    "bg-[#FFFFFF1A] rounded-[8px] h-[48px] font-[500]"
                }}
              />
            </div>
          </div>

          <ProjectFormInput
            title="Discord role id:"
            placeholder="1234567890406187560"
            control={control}
            name="discord_role_id"
            disabled={isEditMode && !!selectedCampaign?.discord_role_id}
          />

          <div>
            <p className="ml-1 mb-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
              Rewards
            </p>

            <Rewards
              onAdd={handleAddReward}
              onChange={handleChangeReward}
              onDelete={handleDeleteReward}
              rewards={rewards}
            />
          </div>
        </form>

        <CampaignsRules />

        <div className="bg-gradient-to-t from-black to-transparent pb-[40px] pt-6 flex items-center w-full left-0 right-0 z-10">
          <Button
            extraClass="w-full"
            type="submit"
            form="campaign-add-form"
            disabled={isSubmitDisabled || isCampaignIteracting}
          >
            <span className="mr-[10px] text-[18px]">
              {selectedCampaign ? "✓" : "+"}
            </span>{" "}
            {selectedCampaign ? "Update campaign" : "Add new campaign"}
          </Button>
        </div>
      </div>
    </>
  );
};
